import { useState } from "react";
import { MapPin, Navigation, TrainFront } from "lucide-react";
import { Section } from "./Section";
import { Reveal } from "./Reveal";
import { ASSETS, DISTANCES, FREIGHT_NOTE, MAP } from "../../config/site";
import { Button } from "../ui/button";

const RINGS = [
  { km: 1.5, r: 26 },
  { km: 16, r: 52 },
  { km: 30, r: 76 },
  { km: 50, r: 100 },
  { km: 100, r: 124 },
];

const DistanceRings = () => (
  <svg viewBox="0 0 280 280" className="mx-auto w-full max-w-[300px]" data-testid="distance-rings">
    {RINGS.map((ring) => (
      <g key={ring.km}>
        <circle cx="140" cy="140" r={ring.r} fill="none" stroke="#C8A974" strokeOpacity="0.45" strokeDasharray="3 4" />
        <text
          x={140 + ring.r * 0.72}
          y={140 - ring.r * 0.72}
          fontSize="9"
          fill="#4A4C4A"
          textAnchor="middle"
        >
          {ring.km} km
        </text>
      </g>
    ))}
    <circle cx="140" cy="140" r="7" fill="#1C3327" stroke="#C8A974" strokeWidth="2" />
    <text x="140" y="128" fontSize="11" fontWeight="600" fill="#1C3327" textAnchor="middle" fontFamily="Cormorant Garamond, serif">
      BIDC
    </text>
  </svg>
);

export const Connectivity = () => {
  const [mapImgOk, setMapImgOk] = useState(true);

  return (
    <Section
      id="location"
      className="bg-sand/50"
      overline="Location & connectivity"
      title="On NH-709, an hour from everywhere that matters"
      sub="Hetampur, Bhiwani–Loharu Road — direct connectivity, the new grain market 16 km away, and the Western Dedicated Freight Corridor within scheduled reach."
    >
      <div className="grid gap-8 lg:grid-cols-5">
        <Reveal className="lg:col-span-3">
          <div className="border border-border bg-paper-light p-2">
            <iframe
              title="BIDC location — Hetampur, Bhiwani, Haryana"
              data-testid="google-map-embed"
              src={MAP.embedUrl()}
              className="h-[340px] sm:h-[420px] w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-3">
              <p className="flex items-center gap-2 text-sm text-[#4A4C4A]">
                <MapPin className="h-4 w-4 text-gold" /> Hetampur, Bhiwani–Loharu Road, NH-709
              </p>
              <Button
                asChild
                variant="outline"
                size="sm"
                data-testid="get-directions-button"
                className="border-forest text-forest hover:bg-forest hover:text-paper transition-colors duration-200"
              >
                <a href={MAP.directionsUrl()} target="_blank" rel="noopener noreferrer">
                  <Navigation className="mr-2 h-4 w-4" /> Get Directions
                </a>
              </Button>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-2">
          <div className="h-full border border-border bg-paper-light p-6">
            {mapImgOk ? (
              <img
                src={ASSETS.mapGraphic.local}
                onError={() => setMapImgOk(false)}
                alt="BIDC connectivity map"
                loading="lazy"
                className="mx-auto w-full object-contain"
                data-testid="map-graphic"
              />
            ) : (
              <DistanceRings />
            )}
            <ul className="mt-6 space-y-2.5">
              {DISTANCES.map((d) => (
                <li
                  key={d.label}
                  className="flex items-center justify-between border-b border-border/70 pb-2.5 text-sm"
                  data-testid={`distance-${d.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <span className="text-[#4A4C4A]">{d.label}</span>
                  <span className="font-display text-lg font-medium text-forest">{d.km} km</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 flex gap-2.5 text-xs leading-relaxed text-[#4A4C4A]/80">
              <TrainFront className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              {FREIGHT_NOTE}
            </p>
          </div>
        </Reveal>
      </div>
    </Section>
  );
};
